import type { MouseEvent, PointerEvent } from 'react'
import { useRef } from 'react'

import { useEvent } from './useEvent'

/**
 * Pointer-based long-press: fires after 450 ms of a steady primary-pointer
 * press and suppresses the click synthesized on release. Movement past 10 px,
 * pointer loss, or release before the threshold cancels.
 */
export const useLongPress = (onLongPress: () => void) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const originRef = useRef<{ x: number; y: number } | null>(null)
  const firedRef = useRef(false)

  const cancel = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    originRef.current = null
  }

  const onPointerDown = useEvent((event: PointerEvent) => {
    if (!event.isPrimary) return
    firedRef.current = false
    originRef.current = { x: event.clientX, y: event.clientY }
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      firedRef.current = true
      onLongPress()
    }, LONG_PRESS_MS)
  })

  const onPointerMove = useEvent((event: PointerEvent) => {
    const origin = originRef.current
    if (!origin || !timerRef.current) return
    if (Math.hypot(event.clientX - origin.x, event.clientY - origin.y) > MOVE_TOLERANCE_PX) cancel()
  })

  const onPointerUp = useEvent(() => cancel())

  const onPointerCancel = useEvent(() => cancel())

  const onClickCapture = useEvent((event: MouseEvent) => {
    if (!firedRef.current) return
    firedRef.current = false
    event.preventDefault()
    event.stopPropagation()
  })

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onClickCapture }
}

const LONG_PRESS_MS = 450
const MOVE_TOLERANCE_PX = 10
